import { Component, computed, effect, input, viewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { applyMarkerOffset } from './marker-offset';

/** Dernière valeur d'une grandeur de baromètre. */
export interface BarometerValueView {
  value: number;
  /** ISO-8601, heure de réception serveur. */
  timestamp: string;
}

/**
 * Dernières mesures d'un baromètre, par grandeur (contrat-baro v2, ADR-020) — horodatages
 * indépendants, entrée absente si jamais reçue. Structurellement identique à `LastBaro`
 * (domain-trackers), que shared-ui (`type:ui`) ne peut pas importer.
 */
export interface BarometerReadingView {
  pressure?: BarometerValueView;
  temperature?: BarometerValueView;
  battery?: BarometerValueView;
}

/** Ce que le marqueur a besoin de savoir d'un baromètre (sous-ensemble structurel de `Tracker`). */
export interface BarometerMarkerData {
  id?: string;
  name?: string | null;
  deviceId?: string | null;
  status?: string | null;
  fixedLatitude?: number | null;
  fixedLongitude?: number | null;
  fixedAltitude?: number | null;
}

/**
 * Marqueur d'un baromètre (`kind = BAROMETER`) sur une carte MapLibre : icône dédiée à sa position
 * fixe, popup au clic avec la dernière pression / température / batterie, chacune avec son heure. Couleur hors de la
 * palette des canaux radio (un baromètre n'est pas un canal).
 */
@Component({
  selector: 'lib-map-barometer-marker',
  standalone: true,
  imports: [MarkerComponent, PopupComponent, FastSvgComponent, DecimalPipe, DatePipe],
  template: `
    @if (hasPosition()) {
      <mgl-marker #baroMarker [lngLat]="[barometer().fixedLongitude!, barometer().fixedLatitude!]">
        <button
          type="button"
          class="relative flex size-7 items-center justify-center rounded-full border-2 border-white text-white shadow-md transition-opacity dark:border-slate-800"
          [class]="alert() ? 'bg-primary-500' : 'bg-teal-500'"
          [class.opacity-30]="dimmed()"
          [attr.aria-label]="barometer().name">
          <fast-svg name="barometer" size="70%" />
          @if (floorTag(); as tag) {
            <span
              class="absolute left-1/2 top-full mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-1 text-[10px] font-bold leading-4 text-white"
              >{{ tag }}</span
            >
          }
        </button>
      </mgl-marker>

      <mgl-popup [offset]="18" [marker]="baroMarker" [closeButton]="false" [closeOnClick]="true" [maxWidth]="'260'">
        <h4 class="text-base font-semibold">{{ barometer().name || barometer().deviceId }}</h4>
        @if (hasReading()) {
          @let r = reading()!;
          @if (r.pressure; as p) {
            <div class="mt-1 flex items-baseline gap-2">
              <span class="text-2xl font-bold tabular-nums">
                {{ p.value | number: '1.1-1' }} <span class="text-sm font-normal text-gray-500">hPa</span>
              </span>
              <span class="text-xs text-gray-500">{{ p.timestamp | date: 'dd/MM HH:mm:ss' }}</span>
            </div>
          }
          <div class="mt-1 flex flex-col text-xs text-gray-600">
            @if (r.temperature; as t) {
              <span>
                {{ t.value | number: '1.1-1' }} °C
                <span class="text-gray-500">· {{ t.timestamp | date: 'dd/MM HH:mm:ss' }}</span>
              </span>
            }
            @if (r.battery; as bat) {
              <span>
                {{ bat.value | number: '1.0-0' }} %
                <span class="text-gray-500">· {{ bat.timestamp | date: 'dd/MM HH:mm:ss' }}</span>
              </span>
            }
          </div>
        } @else {
          <div class="mt-1 text-xs text-gray-500">{{ waitingLabel() }}</div>
        }
        <div class="mt-2 text-xs text-gray-500">
          {{ barometer().fixedLatitude?.toFixed(6) }}, {{ barometer().fixedLongitude?.toFixed(6) }}
          @if ((barometer().fixedAltitude ?? null) !== null) {
            · {{ barometer().fixedAltitude?.toFixed(1) }} m
          }
        </div>
      </mgl-popup>
    }
  `,
})
export class MapBarometerMarkerComponent {
  barometer = input.required<BarometerMarkerData>();
  /** Dernière mesure connue (live STOMP ou `Tracker.lastBaro`), sinon « en attente ». */
  reading = input<BarometerReadingView | null>(null);
  /** Vrai = alerte : le marqueur passe en couleur primaire. */
  alert = input<boolean>(false);
  waitingLabel = input<string>('Waiting for a reading…');
  /**
   * Décalage écran (px) du marqueur posé au sol : le montre à son altitude sur une carte inclinée
   * avec un bâtiment 3D (ADR-017). `mgl-marker` ne relit pas son `offset` après création : il est
   * appliqué au marqueur MapLibre.
   */
  offset = input<[number, number] | null>(null);
  /** Estompé : autre étage que celui affiché. */
  dimmed = input<boolean>(false);
  /** Étiquette sous le marqueur (étage), ou `null`. */
  floorTag = input<string | null>(null);
  private readonly mglMarker = viewChild(MarkerComponent);

  constructor() {
    effect(() => {
      const offset = this.offset();
      const marker = this.mglMarker()?.markerInstance();
      applyMarkerOffset(marker, offset);
    });
  }

  protected readonly hasReading = computed(() => {
    const r = this.reading();
    return !!(r?.pressure || r?.temperature || r?.battery);
  });

  protected readonly hasPosition = computed(
    () => typeof this.barometer().fixedLongitude === 'number' && typeof this.barometer().fixedLatitude === 'number'
  );
}
